<div class="header">
	<div class="content">
		<div class="contentInner">
			<div class="pageTitle" appReveal>
			<h1 class="headline">Conference Room Scheduler & Meeting Room Display</h1>
			</div>
		</div>
	</div>
</div>
<div class="content">
	<div class="contentInner">
	  <h2 id="calendar" class="sectionHeader">Dash works with your calendars</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Dash integrates directly with Microsoft 365 and Google Workspace.  See each room's full schedule on your Dash
          display, and book meetings from Outlook and Google Calendar, or directly from the display.  For more information,
          please see our <a routerLink="/guide/officeSetup">Microsoft 365</a> and <a routerLink="/guide/googleSetup">Google Workspace</a> setup guides.
        </p>
        <h3>Dash works with Exchange, and other calendar systems</h3>
        <p>
          Dash also works with any system supported by your device's built-in calendar, such as on-premises Exchange servers
          and CalDAV.  For more information, please see our <a routerLink="/guide/deviceSetup" fragment="calendarSetup">Device setup</a> guide.
        </p>
        <p class="tip icon">
          Unlike other meeting room displays, which route all of your meeting room information through their central servers,
          Dash is designed so that each display communicates with your meeting room calendars directly.  This means that your
          sensitive information such as meeting titles, descriptions, and attendees never pass through our servers.
        </p>
      </div>
      <app-illustration alt="Setting up a calendar with Dash on an iOS device" webpSrc="/assets/images/calendar-screenshot-wide_2x.webp" src="/assets/images/calendar-screenshot-wide_2x.png" [clickable]="false" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <h2 id="display" class="sectionHeader">Your room status at a glance</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Dash provides a simple overview of your meeting room, with a colored status to show you if the room is busy or
          free from a distance, and a full schedule up close.  The display is highly customizable:
        </p>
        <ul>
          <li>Show the status color behind the schedule, on a border around the display, or on the background of the whole display.</li>
          <li>Display the name of the room, the current meeting, or the time most prominently</li>
          <li>Show a calendar view of the day's schedule, or a list of one or several days meetings</li>
          <li>Over 30 customizable options</li>
        </ul>

        <p>Dash offers five display modes:</p>
        <ul>
          <li>A room display, ideal for outside the room</li>
          <li>A countdown display, showing the time left in a meeting, ideal for inside the room</li>
          <li>Three multi-room display modes: Dashboard, Schedule dashboard, and What's on, ideal for reception or shared spaces</li>
        </ul>

        <p>For more information about each display mode and how to customize the Dash display, please see our <a routerLink="/guide/usingDash">online guide</a>.</p>

        <p class="tip icon">When using one of the multi-room display modes, you can show multiple rooms on a single display, and pay for only one device.</p>
      </div>
      <app-slideshow class="illustration" delay="4000" width="900" height="675">
        <app-slide alt="The Dash room display" webpSrc="/assets/images/display-screenshot-wide_2x.webp" src="/assets/images/display-screenshot-wide_2x.png"></app-slide>
        <app-slide alt="The Dash countdown display" webpSrc="/assets/images/countdown-screenshot-wide_2x.webp" src="/assets/images/countdown-screenshot-wide_2x.png"></app-slide>
        <app-slide alt="The Dash dashboard display" webpSrc="/assets/images/dashboard-screenshot-wide_2x.webp" src="/assets/images/dashboard-screenshot-wide_2x.png"></app-slide>
        <app-slide alt="The Dash schedule dashboard display" webpSrc="/assets/images/schedule-dashboard-screenshot-wide_2x.webp" src="/assets/images/schedule-dashboard-screenshot-wide_2x.png"></app-slide>
        <app-slide alt="The Dash What's on display" webpSrc="/assets/images/whats-on-screenshot-wide_2x.webp" src="/assets/images/whats-on-screenshot-wide_2x.png"></app-slide>
      </app-slideshow>
    </div>

    <h2 id="booking" class="sectionHeader">Simple room booking</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Dash makes booking your room simple.  Book the room using Outlook or Google Calendar if you're at your desk, or
          book directly from the display. Book the room instantly in one tap (just pick how long you need it),
          or use the calendar view to book a later time.
        </p>
        <p>
          You can optionally configure Dash so that people booking from the display can set themselves as the organizer,
          add their own title and description, and add attendees to meetings from your company's user directory.
          Attendees will be sent an invitation with a video call link, so that people working from home can join remotely.
          You can also add  external attendees by email address.
        </p>
        <p>Extend a meeting in one tap if it runs over, or end it early to free the room for others.</p>
        <p>If the room is booked, use Dash to find a free room close by and book it right from the display.  The room
          will be ready and waiting for you when you get there.</p>
        <p>
          Dash's room booking is fully customizable, so all features can be controlled or turned on and off.  For more
          information, please see our <a routerLink="/guide/usingDash" fragment="bookingAndCheckIn">online guide</a>.
        </p>
      </div>
      <app-slideshow class="illustration" delay="4000" width="900" height="675">
        <app-slide alt="Room booking in Dash meeting room display" webpSrc="/assets/images/booking-screenshot-wide_2x.webp" src="/assets/images/booking-screenshot-wide_2x.png" alt="Booking a room in Dash"></app-slide>
        <app-slide alt="Add attendees and custom message to a booking in Dash" webpSrc="/assets/images/booking-message-screenshot-wide_2x.webp" src="/assets/images/booking-message-screenshot-wide_2x.png" alt="Booking a room in Dash"></app-slide>
        <app-slide alt="Extend meetings and end them early in Dash meeting room display" webpSrc="/assets/images/extend-end-screenshot-wide_2x.webp" src="/assets/images/extend-end -screenshot-wide_2x.png" alt="Booking a room in Dash"></app-slide>
        <app-slide alt="Find free rooms in Dash meeting room display" webpSrc="/assets/images/findrooms-screenshot-wide_2x.webp" src="/assets/images/findrooms-screenshot-wide_2x.png" alt="Finding a free room in Dash"></app-slide>
      </app-slideshow>
    </div>

    <h2 id="checkIn" class="sectionHeader">Stop no-shows with Dash's check-in</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          No-shows waste some of the most valuable space in any organization.  Dash's check-in feature allows
          you to reclaim your meeting rooms when no one shows up.
        </p>
        <p>
          When the check-in feature is enabled, Dash displays a check in button on the main display.
          If the button is not pressed within a set amount of time, Dash releases the room, making it available to
          others.  You can also set Dash to release the room from recurring series after a set number of
          missed check-ins in a row.
        </p>
        <p class="tip icon">If a check-in is missed, Dash releases the room from the meeting but leaves the meeting
          itself in place, so if there is an online call or a booking in another room it can continue as normal.</p>
      </div>
      <app-illustration alt="Check in a meeting with Dash meeting room display" webpSrc="/assets/images/checkin-screenshot-wide_2x.webp" src="/assets/images/checkin-screenshot-wide_2x.png" [clickable]="false" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <h2 id="branding" class="sectionHeader">Showcase your brand</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Customize Dash's appearance to match your organization's style and showcase your brand identity to staff and visitors.
        </p>
        <p>Set a custom logo, background image, and colors for all of Dash's visible
          elements. A live preview lets you see how your images and colors look instantly.
        </p>
        <p class="tip icon">Combine custom branding with <a routerLink="/features" fragment="display">Dash's display customization options</a> to fully control
          how your Dash device looks and behaves.</p>
      </div>
      <app-illustration alt="Custom branding in Dash meeting room display" webpSrc="/assets/images/branding-screenshot-wide_2x.webp" src="/assets/images/branding-screenshot-wide_2x.png" [clickable]="false" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <h2 id="enterprise" class="sectionHeader">Central management and analytics</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Dash Enterprise lets you configure every display from one place instead of individually on each device.  Deploy
          and configure Dash through our web management dashboard or your MDM system of choice.
        </p>
        <p>Learn how your meeting spaces are being used with detailed room analytics.  Our interactive charts break down
          meeting statistics and help you measure efficiency with ease.</p>
        <p>Dash Enterprise also adds a fully-featured web version of Dash, compatible with smart TVs and commercial displays.</p>
        <p class="tip icon">Dash Enterprise is available as an annual subscription, with optional invoice billing.  <a routerLink="/contact">Contact us</a> to get started with a one-month free trial.</p>
        <a routerLink="/about-enterprise" [queryParams]="{ utm_source: 'features'}" class="button" style="margin-top: 0">Learn about Dash Enterprise</a>
      </div>
      <app-illustration alt="Dash enterprise web dashboard" src="/asssets/images/dash-enterprise-configuration-screenshot-horizontal_2x.png" webpSrc="/assets/images/dash-enterprise-configuration-screenshot-horizontal_2x.webp" [clickable]="false" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <div class="faq">
      <h2 id="faq" class="sectionHeader">Frequently asked questions</h2>
      <h3>How much does a meeting room display cost?</h3>
      <p>Dash offers a number of solutions to suit any budget.
        <ul>
          <li>Dash Free gives you a read-only room display completely free of charge</li>
          <li>Dash Business adds room booking, find free rooms, check-in, custom branding, and more for $3.99 USD per device per month.  Start a subscription from within the app - your first month is free.</li>
          <li>Dash Enterprise adds central management of your Dash devices and their configuration, detailed room analytics, and a web version of Dash for smart TVs and commercial displays.  Dash Enterprise is competitively priced - please <a routerLink="/contact">contact us</a> to get started, including a one-month free trial.</li>
        </ul>
        <p>You can find more information about each plan and how to subscribe on our <a routerLink="/pricing">pricing page</a>.
      </p>

      <h3>Can I use an iPad or Android tablet as a meeting room display?</h3>
      <p>Yes!  Dash is available in the Apple App Store, Google Play Store, and Amazon App Store, supporting any
        Android/Amazon device running Android 5 and above, and any iPhone or iPad running iOS 12 and above.  Dash also
        supports commercial room displays, including models from our hardware partners that come with Dash preinstalled.
        For more information about the devices we support, see our <a routerLink="/hardware">hardware guide</a></p>

      <h3>I don't have power wired where I need my displays to go.  Can I still use Dash?</h3>
      <p>Yes!  We have a number of options you can explore:</p>
      <ul>
        <li>Our hardware partners offer dedicated room display devices that can be powered using PoE, so if you have Ethernet nearby you can power your display using a PoE injector.  See our <a routerLink="/hardware">hardware guide</a> for more information.</li>
        <li>Consider where you can mount your displays.  Dash offers a number of alternative display modes, for example the countdown mode is suitable for a device
          mounted inside the room.  Our three multi-room dashboard modes allow you to use a single device, mounted
          in a central location, to show the status of several meeting rooms at once.</li>
        <li>Larger tablets such as iPads can be configured to give several days of battery life, so if you can charge your
          displays overnight they don't need to be mounted near an outlet.</li>
      </ul>

      <h3>Does Dash work with Microsoft 365 and Google Workspace?</h3>
      <p>Dash integrates directly with Microsoft 365 and Google Workspace, creating a seamless experience.  Book meetings
      in Outlook or Google Calendar and have them show up on your Dash displays, or book the room directly from Dash.
      You can find more information in our <a routerLink="/guide">online guide</a>.</p>

      <h3>I use an on-premises Exchange server, or a different service to Microsoft 365 and Google Workspace.  Will Dash still work?</h3>
      <p>Dash can also work with any service that your device's built-in calendar supports.  This includes
        on-premises Exchange servers and other services through standards like CalDAV.  If you can get your meeting room
        schedules into your device's built-in calendar then Dash can work with them.  If you're in doubt or need help
        setting this up, please <a routerLink="/contact">Contact Dash support</a>.</p>

      <h3>Can one display show more than one room?</h3>
      <p>Dash offers 3 multi-room dashboard displays which can display multiple rooms on a single device.  Our Dashboard
        display modes support all of Dash's features, such as room booking and check-in, so you don't lose any functionality.</p>
      <p>Our Dashboard modes are ideal for a larger device mounted in a central location, or a display mounted between two rooms.</p>

      <h3>How many meeting rooms do I need for Dash Enterprise?</h3>
      <p>Dash Enterprise has advantages for businesses of any size, in fact we have many customers using Dash Enterprise
        with just one or two meeting rooms.  Dash Enterprise is essential for larger organizations, but also offers a
        number of advantages for small businesses:</p>
      <ol>
        <li>Your Dash configuration is stored and backed up centrally, so if a device fails you can replace and reconfigure it in one step</li>
        <li>Easily see the status of your Dash devices and get notified if one goes offline</li>
        <li>Push configuration changes without having to walk to each device</li>
        <li>Detailed room analytics provide insights for a single room</li>
        <li>Dash Enterprise is an annual subscription, supporting invoice billing and purchase orders, which can be a more convenient way to pay for your business.</li>
      </ol>
      <p>Dash Enterprise is competitively priced - please <a routerLink="/contact">contact us</a> for more information.</p>

      <h3>Is my meeting information private?</h3>
      <p>
        We take the security of your information seriously.  Unlike other meeting room displays, which route all of your meeting room information
        through their central servers, each Dash display communicates with your meeting room calendars directly.  This means
        that your sensitive information such as meeting titles, descriptions, and attendees never pass through our servers.
      </p>
    </div>

  </div>
